import { DrawerId, useDrawer } from 'common/components';
import { ROUTES } from 'common/constants';
import {
  AddIcon,
  BalanceAIColoredIcon,
  HomeIcon,
  HomeOutlinedIcon,
  MenuIcon,
} from 'common/icons';
import { useNavigateWithOrgAndFarm } from 'common/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from 'src/auth';
import { useNotificationUnreadCount } from 'src/pages/notifications';
import { useOrganization } from 'src/pages/organization';
import EntitlementEnum from 'src/pages/organization/entitlements.enum';

import { useQuickActionList } from '../quick-action-list.hooks';
import * as Styled from './footer.style';
import { QuickAction } from './quick-action/quick-action.component';

interface FooterComponentType {
  isNavbarOpen: boolean;
  onHomeClick: () => void;
  onMenuClick: () => void;
  onCropwiseAiOpen?: () => void;
}

const FooterComponent: FC<FooterComponentType> = ({
  isNavbarOpen,
  onHomeClick,
  onMenuClick,
  onCropwiseAiOpen,
}) => {
  const [searchParams] = useSearchParams();

  const isBalanceAiOpen = searchParams.get('balanceAi[open]') === 'true';

  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigateWithOrgAndFarm();
  const location = useLocation();
  const { entitlements, currentOrganizationId } = useOrganization();
  const featureFlags = useFlags();
  const notificationUnreadCount = useNotificationUnreadCount();
  const hasUnreadNotifications =
    featureFlags.headerNotification && notificationUnreadCount > 0;
  const { user } = useAuth();
  const { open: openDrawer } = useDrawer();
  const homeRoute = ROUTES.MY_CROPWISE;

  useEffect(() => {
    if (isBalanceAiOpen) {
      openDrawer(DrawerId.BalanceAi);
    }
  }, [isBalanceAiOpen, openDrawer]);

  const onClose = () => {
    setOpen(false);
  };

  const { quickActions } = useQuickActionList(onClose);

  const quickActionItems = quickActions
    .filter(
      (value) =>
        !featureFlags[value.feature_flag_disable ?? ''] &&
        (value.entitlement === undefined ||
          entitlements.includes(value.entitlement)),
    )
    .map((item, index) => {
      const itemKey = `action-${index}`;

      return (
        <QuickAction
          key={itemKey}
          icon={item.icon}
          description={item.description}
          onClick={item.onClick}
        />
      );
    });

  const onGoHome = useCallback(() => {
    onHomeClick();
    navigate(homeRoute, null, null);
  }, [navigate, onHomeClick, homeRoute]);

  const handleHomeIcon = useMemo(() => {
    return location.pathname === homeRoute ? (
      <HomeIcon />
    ) : (
      <HomeOutlinedIcon />
    );
  }, [location.pathname, homeRoute]);

  const handleQuickActionsButton = useMemo(() => {
    if (!user || !currentOrganizationId) return null;

    if (
      featureFlags.balanceAiFms1865QuickActions &&
      entitlements.includes(EntitlementEnum.BALANCE_AI_VOICE_ACTIONS)
    ) {
      return (
        <Styled.QuickActionsButtonAI
          shape="circle"
          type="default"
          size="large"
          data-testid="balance-ai-quick-actions-button"
          onClick={() => {
            if (onCropwiseAiOpen) {
              onCropwiseAiOpen();
            } else {
              openDrawer(DrawerId.BalanceAi);
            }
          }}
          id="balance-ai-quick-actions-button"
        >
          <BalanceAIColoredIcon />
        </Styled.QuickActionsButtonAI>
      );
    } else {
      return (
        <Styled.QuickActionsButton
          shape="circle"
          type="primary"
          size="large"
          onClick={() => setOpen(true)}
        >
          <AddIcon />
        </Styled.QuickActionsButton>
      );
    }
  }, [
    user,
    onCropwiseAiOpen,
    featureFlags.balanceAiFms1865QuickActions,
    currentOrganizationId,
    entitlements,
    openDrawer,
  ]);

  return (
    <Styled.FooterLayout>
      <Styled.HomeButton
        type="text"
        shape="round"
        size="large"
        onClick={onGoHome}
        active={location.pathname === homeRoute}
      >
        {handleHomeIcon}
        {t('footer.home')}
      </Styled.HomeButton>
      {handleQuickActionsButton}
      <Styled.MenuButton
        type="text"
        shape="round"
        size="large"
        active={isNavbarOpen}
        onClick={onMenuClick}
      >
        <Styled.MenuIconWrapper>
          <MenuIcon />
          {hasUnreadNotifications && (
            <Styled.UnreadBadge data-testid="footer-menu-unread-badge" />
          )}
        </Styled.MenuIconWrapper>
        {t('footer.menu')}
      </Styled.MenuButton>
      <Styled.QuickActionsDrawer
        title={t('footer.quick_actions')}
        placement="bottom"
        width="100vw"
        onClose={onClose}
        open={open}
        closable={false}
        height={300}
      >
        {quickActionItems}
      </Styled.QuickActionsDrawer>
    </Styled.FooterLayout>
  );
};

export { FooterComponent };
